import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Field, FieldLabel } from '@quebon/ui/components/field'
import { RadioGroup, RadioGroupItem } from '@quebon/ui/components/radio-group'
import type { ReviewNoteSourceLessonView } from '@/api/lcReviewNote'
import { filterSelectableSourceLessonIds, getReviewNoteSourceCounts } from '@/utils/reviewNoteSources'
import { ReviewNoteSourceSelector } from './ReviewNoteSourceSelector'

interface Props {
  lessons: ReviewNoteSourceLessonView[]
  selectedLessonIds: number[]
  anchorLessonId: number
  onChange: (lessonIds: number[]) => void
  disabled?: boolean
}

type RangeMode = 'current' | 'custom'

export function ReviewNoteRangePicker({ lessons, selectedLessonIds, anchorLessonId, onChange, disabled = false }: Props) {
  const { t } = useTranslation('coaching')
  const id = useId()
  const titleId = `${id}-title`
  const treeId = `${id}-tree`
  const selectedIds = filterSelectableSourceLessonIds(selectedLessonIds, lessons)
  const [mode, setMode] = useState<RangeMode>(() => selectedIds.length === 1 && selectedIds[0] === anchorLessonId ? 'current' : 'custom')
  const customSelection = useRef(selectedIds)
  const anchorSelectable = filterSelectableSourceLessonIds([anchorLessonId], lessons).length > 0
  const selected = new Set(selectedIds)
  const counts = getReviewNoteSourceCounts(lessons.filter((lesson) => selected.has(lesson.lessonId)))
  const summary = counts.lessonCount === 0
    ? t('reviewNote.rangeEmpty')
    : counts.chapterCount > 0
      ? t('reviewNote.rangeSummary', counts)
      : t('reviewNote.legacyRangeSummary', counts)

  const changeMode = (value: RangeMode) => {
    if (disabled || value === mode) return
    if (value === 'current') {
      if (!anchorSelectable) return
      customSelection.current = selectedIds
      setMode('current')
      onChange([anchorLessonId])
    } else {
      setMode('custom')
      onChange(filterSelectableSourceLessonIds(customSelection.current, lessons))
    }
  }

  return (
    <Card size="sm" className="min-w-0" aria-label={t('reviewNote.rangeLabel')}>
      <CardHeader>
        <CardTitle id={titleId}>{t('reviewNote.rangeLabel')}</CardTitle>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-4">
        <RadioGroup
          name={`${id}-mode`}
          value={mode}
          onValueChange={changeMode}
          disabled={disabled}
          aria-labelledby={titleId}
          className="flex flex-wrap gap-x-6 gap-y-3"
        >
          <Field orientation="horizontal" data-disabled={disabled || !anchorSelectable} className="w-auto">
            <RadioGroupItem id={`${id}-current`} value="current" disabled={!anchorSelectable} />
            <FieldLabel htmlFor={`${id}-current`}>{t('reviewNote.currentLessonOnly')}</FieldLabel>
          </Field>
          <Field orientation="horizontal" data-disabled={disabled} className="w-auto">
            <RadioGroupItem id={`${id}-custom`} value="custom" aria-controls={treeId} />
            <FieldLabel htmlFor={`${id}-custom`}>{t('reviewNote.customRange')}</FieldLabel>
          </Field>
        </RadioGroup>
        <div id={treeId} hidden={mode !== 'custom'}>
          {mode === 'custom' ? (
            <div className="flex min-w-0 flex-col gap-3">
              <p className="break-words text-sm font-medium tabular-nums" aria-live="polite">{summary}</p>
              <ReviewNoteSourceSelector
                hideHeading
                lessons={lessons}
                selectedLessonIds={selectedIds}
                onChange={(ids) => {
                  customSelection.current = ids
                  onChange(ids)
                }}
                disabled={disabled}
              />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
